import { useLocation } from '@tanstack/react-router';
import { BookingPageSkeleton } from './BookingPageSkeleton';
import { ProfilePageSkeleton } from './ProfilePageSkeleton';

/**
 * Route-aware skeleton loader for the initial app loading phase.
 *
 * ## Skeleton Strategy
 *
 * This component is used in `__root.tsx` during the initial auth initialization phase.
 * Different routes have different skeleton strategies:
 *
 * ### Full-page skeletons (handled here):
 * - `/booking` - Shows BookingPageSkeleton (two-column layout with calendar)
 * - `/profile` - Shows ProfilePageSkeleton (header with user info + booking list)
 *
 * ### Routes that handle their own loading (return null here):
 * - `/admin/*` and `/stats` - These routes render their own header immediately, then
 *   show AdminContentSkeleton or StatsContentSkeleton for the data area. This prevents
 *   a flash of skeleton header before the actual header appears.
 * - `/changelog` - Static content with no loading state needed
 *
 * ### Default fallback:
 * - Other routes use BookingPageSkeleton as a sensible default
 *
 * ## Usage
 *
 * Used in `__root.tsx` alongside the real `Navbar` (with skeleton prop) during `!initialized` phase:
 * ```tsx
 * <Navbar skeleton />
 * <RouteSkeletonLoader />
 * ```
 *
 * Note: The Navbar handles its own skeleton states internally for dynamic parts
 * (office selector, profile dropdown), ensuring the header stays static while
 * only content areas show skeleton loading states.
 */
export function RouteSkeletonLoader() {
  const location = useLocation();
  const pathname = location.pathname;

  // Map routes to their skeleton components
  if (pathname === '/booking' || pathname.startsWith('/booking/')) {
    return <BookingPageSkeleton />;
  }

  if (pathname === '/profile') {
    return <ProfilePageSkeleton />;
  }

  // These routes render their own header, then a content skeleton.
  // Returning null here prevents a brief booking-skeleton flash before that header appears.
  if (pathname.startsWith('/admin') || pathname === '/stats') {
    return null;
  }

  // Changelog is static content, no skeleton needed
  if (pathname === '/changelog') {
    return null;
  }

  // Default fallback for other routes
  return <BookingPageSkeleton />;
}
